import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, ArrowRight } from "lucide-react";
import { useAuth } from "../lib/authContext";
import { assistantApi } from "../lib/assistantApi";
import { BibleRefLink } from "../lib/bibleRef";
import { getDailyVerseRef } from "../lib/dailyVerse";
import { fetchVerse } from "../lib/bible";

/**
 * Daily AI Reflection card, sits between the verse-of-the-day and the devotional.
 *
 * Authenticated: GET /api/assistant/daily (cached per user/day in Mongo).
 * Anonymous: shows today's rotated verse from the 30-list + a CTA to create
 *   an account (no LLM call).
 * Failure (LLM down): silently falls back to the rotated verse.
 */
export default function DailyAIReflectionCard() {
  const { isAuthenticated, isChecking } = useAuth();
  const [data, setData] = useState(null); // {verse_ref, verse_text, reflection, mode}
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isChecking) return;
    let cancelled = false;

    const loadFallback = async () => {
      try {
        const r = getDailyVerseRef();
        const v = await fetchVerse(r.bookSlug, r.chapter, r.verse);
        if (!cancelled) {
          if (v) {
            setData({
              verse_ref: `${v.book_name} ${v.chapter}:${v.verse}`,
              verse_text: v.text,
              reflection: null,
              mode: "fallback",
            });
          } else {
            setData(null);
          }
        }
      } catch {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    if (isAuthenticated) {
      assistantApi
        .daily()
        .then((d) => {
          if (cancelled) return;
          setData({
            verse_ref: d.verse_ref,
            verse_text: d.verse_text,
            reflection: d.reflection,
            mode: "ai",
          });
          setLoading(false);
        })
        .catch(() => {
          // silent fallback to rotated verse
          loadFallback();
        });
    } else {
      loadFallback();
    }

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, isChecking]);

  if (loading || !data) return null;

  const starter = data.reflection
    ? `Quero conversar mais sobre ${data.verse_ref}.`
    : "";

  return (
    <section
      data-testid="home-ai-reflection-card"
      data-mode={data.mode}
      className="bg-white rounded-2xl border border-gold/40 shadow-sm overflow-hidden mb-6"
    >
      <div className="bg-[hsl(var(--parchment))] px-5 sm:px-7 pt-5 pb-4 flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-white/80 flex items-center justify-center shrink-0">
          <Sparkles className="w-4 h-4 text-gold" />
        </div>
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-[0.3em] text-gold">Reflexão do Auxiliar Bíblico</p>
          <p className="text-[11px] text-navy/55 mt-0.5">
            {data.mode === "ai" ? "Personalizada para hoje" : "Versículo do dia"}
          </p>
        </div>
      </div>

      <div className="px-5 sm:px-7 py-6 space-y-4">
        <blockquote
          className="font-display text-lg sm:text-xl text-navy leading-snug italic"
          data-testid="home-ai-reflection-verse"
        >
          “{data.verse_text}”
        </blockquote>
        <BibleRefLink
          refText={data.verse_ref}
          testid="home-ai-reflection-verse-ref-link"
          className="text-sm font-medium text-gold tracking-wide inline-block hover:underline hover:text-navy transition-colors"
        />

        {data.reflection && (
          <p
            className="text-[15px] leading-relaxed text-navy/85 whitespace-pre-wrap"
            data-testid="home-ai-reflection-text"
          >
            {data.reflection}
          </p>
        )}

        <div className="pt-1">
          {data.mode === "ai" ? (
            <Link
              to={`/assistente?starter=${encodeURIComponent(starter)}`}
              data-testid="home-ai-reflection-cta"
              className="inline-flex items-center gap-2 text-[11px] uppercase tracking-widest text-navy hover:text-gold transition-colors"
            >
              Conversar mais <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          ) : (
            <Link
              to="/cadastro?next=/"
              data-testid="home-ai-reflection-cta-anon"
              className="inline-flex items-center gap-2 text-[11px] uppercase tracking-widest text-navy hover:text-gold transition-colors"
            >
              Crie conta para uma reflexão personalizada <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          )}
        </div>
      </div>
    </section>
  );
}
